import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getReferralConfig } from "../../evm/referralConfig";
import { evmToNibiAddress } from "../../evm/services/referralService";
import { WhiteButton } from "../Buttons/WhiteButton";
import { BlackButton } from "../Buttons/BlackButton";

// ─── Inline SVG Icons ───

const CopyIcon = ({ className = "" }) => (
  <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = ({ className = "" }) => (
  <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XPostIcon = ({ className = "" }) => (
  <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const CloseIcon = ({ className = "" }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const LinkIcon = ({ className = "" }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const EmptyIcon = ({ className = "" }) => (
  <svg className={className} width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

export default function RefCodesModal({ open, onClose }) {
  const account = useSelector((state) => state.auth.account);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const fetchCodes = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    try {
      const config = getReferralConfig();
      const nibiAddr = account.startsWith("0x")
        ? evmToNibiAddress(account)
        : account;

      const query = `
        query ReferralData($referrer: String!, $trader: String!) {
          referral {
            referralCodes(referrer: $referrer) {
              code
              referrer
              block { block_ts }
            }
            referralRedemption(referrer: $referrer) {
              code
              active
              block { block_ts }
            }
            referralRedemptionTrader(trader: $trader) {
              code
              active
              referrer
              block { block_ts }
            }
          }
        }
      `;

      const res = await fetch(config.keeperUrl, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ query, variables: { referrer: nibiAddr, trader: nibiAddr } }),
      });

      const json = await res.json();
      const rawCodes = json.data?.referral?.referralCodes ?? [];
      const redemptions = json.data?.referral?.referralRedemption ?? [];

      // Count redemptions per code
      const redemptionCounts = {};
      redemptions.forEach((r) => {
        if (r.code) {
          redemptionCounts[r.code] = (redemptionCounts[r.code] || 0) + 1;
        }
      });

      const enriched = rawCodes.map((c) => ({
        code: c.code,
        createdAt: c.block?.block_ts || null,
        referredCount: redemptionCounts[c.code] || 0,
      }));

      setCodes(enriched);
    } catch (err) {
      console.error("Failed to fetch referral codes:", err);
    } finally {
      setLoading(false);
    }
  }, [account]);

  useEffect(() => {
    if (open && account) {
      fetchCodes();
    }
  }, [open, account, fetchCodes]);

  // Prevent scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  const handleCopy = async (code) => {
    const link = `https://app.codedestate.com/?ref=${code}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedCode(code);
    toast.success("Link copied!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleShareX = (code) => {
    const text = encodeURIComponent(
      "Join Coded Estate and trade Real Estate, Watches & more as perpetuals — all on-chain!\n\nUse my referral code:"
    );
    const url = encodeURIComponent(`https://app.codedestate.com/?ref=${code}`);
    window.open(
      `https://x.com/intent/tweet?text=${text}&url=${url}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[99999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-[95vw] sm:w-[90vw] md:w-[460px] max-w-[460px] bg-white rounded-[16px] shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        {/* Header */}
        <div className="h-[3px] bg-gradient-to-r from-[#5b1dee] via-[#8b5cf6] to-[#5b1dee]" />
        <div className="flex items-center justify-between px-5 sm:px-6 pt-5 pb-3">
          <div>
            <h2 className="text-[18px] font-bold text-[#111]">Your Referral Codes</h2>
            <p className="text-[#888] text-[12px] mt-0.5">
              Share your link and earn from every referred trader
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <CloseIcon className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 sm:px-6 pb-6 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="animate-spin w-6 h-6 text-[#5b1dee]" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" />
              </svg>
            </div>
          ) : codes.length === 0 ? (
            <div className="text-center py-10">
              <div className="flex justify-center mb-3">
                <EmptyIcon className="text-[#ccc]" />
              </div>
              <p className="text-[#888] text-[14px] font-medium mb-1">No referral codes yet</p>
              <p className="text-[#aaa] text-[12px]">
                Visit{" "}
                <a href="/claim" className="text-[#5b1dee] hover:underline">
                  /claim
                </a>{" "}
                to create your referral code
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {codes.map((item) => (
                <div
                  key={item.code}
                  className="border border-[#eee] rounded-[12px] p-4 hover:border-[#d4c4f7] transition-colors"
                >
                  {/* Code + stats row */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[#5b1dee] text-[20px] font-bold tracking-wider">
                      {item.code}
                    </span>
                    <span className="text-[12px] text-[#888] bg-[#f5f5f5] px-2.5 py-1 rounded-full">
                      {item.referredCount} {item.referredCount === 1 ? "referral" : "referrals"}
                    </span>
                  </div>

                  {/* Link preview */}
                  <div className="flex items-center gap-1.5 mb-3">
                    <LinkIcon className="text-[#bbb] flex-shrink-0" />
                    <span className="text-[12px] text-[#888] font-mono truncate">
                      app.codedestate.com/?ref={item.code}
                    </span>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <div className="flex-1" onClick={() => handleCopy(item.code)}>
                      <WhiteButton
                        text={
                          <span className="flex items-center justify-center gap-1.5 text-[12px]">
                            {copiedCode === item.code ? (
                              <>
                                <CheckIcon className="text-[#22c55e]" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <CopyIcon />
                                Copy Link
                              </>
                            )}
                          </span>
                        }
                        className="w-full py-[6px]"
                      />
                    </div>
                    <div className="flex-1" onClick={() => handleShareX(item.code)}>
                      <BlackButton
                        text={
                          <span className="flex items-center justify-center gap-1.5 text-[12px]">
                            <XPostIcon />
                            Share on X
                          </span>
                        }
                        className="w-full py-[6px]"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
